import React, { memo, Fragment, useState, useMemo } from 'react'
import { gql, useQuery, useMutation } from '@apollo/client'
import { Row, Col, Table, Button, Badge, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { GraphQLError } from 'graphql'
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    flexRender,
    createColumnHelper,
    SortingState,
    ColumnDef,
} from '@tanstack/react-table'
import Card from '../components/bootstrap/Card'
import { CubeLoader } from '@/components/ui'
import type {
    User,
    GetAllUsersData,
    GetAllUsersVariables,
    ApproveUserData,
    ApproveUserVariables,
} from '../types/user'

const GET_ALL_USERS = gql`
    query GetAllUsers($limit: Int, $page: Int) {
        getAllUsers(limit: $limit, page: $page) {
            id
            firstName
            lastName
            businessName
            email
            phone
            createdAt
            onboarding
            role
        }
    }
`

const APPROVE_USER = gql`
    mutation ApproveUser($userId: ID!) {
        approveUser(userId: $userId) {
            id
            firstName
            lastName
            businessName
            email
            phone
            createdAt
            onboarding
            role
        }
    }
`

const columnHelper = createColumnHelper<User>()

const Users = memo(() => {
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [limit] = useState<number>(20)
    const [errors, setErrors] = useState<readonly GraphQLError[]>([])
    const [sorting, setSorting] = useState<SortingState>([])

    const { loading, error, data, refetch } = useQuery<GetAllUsersData, GetAllUsersVariables>(
        GET_ALL_USERS,
        {
            variables: { limit, page: currentPage },
            fetchPolicy: 'network-only',
        }
    )

    const [approveUser] = useMutation<ApproveUserData, ApproveUserVariables>(APPROVE_USER, {
        onCompleted: () => {
            // Refetch users to update the list
            refetch()
        },
        onError: ({ graphQLErrors }) => {
            setErrors(graphQLErrors)
        },
    })

    const handleApprove = (userId: string): void => {
        setErrors([])
        approveUser({ variables: { userId } })
    }

    const formatDate = (dateString: string): string => {
        if (!dateString) return ''
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        })
    }

    const isApproveButtonEnabled = (user: User): boolean => {
        return !!(user.firstName && user.businessName && user.phone)
    }

    const getMissingFields = (user: User): string[] => {
        const missing: string[] = []
        if (!user.firstName) missing.push('Name')
        if (!user.businessName) missing.push('Business Name')
        if (!user.phone) missing.push('Phone')
        return missing
    }

    const renderApproveButton = (user: User): React.ReactNode => {
        if (!user.onboarding) {
            return null
        }

        const enabled = isApproveButtonEnabled(user)
        const button = (
            <Button
                size="sm"
                variant={enabled ? 'success' : 'secondary'}
                disabled={!enabled}
                onClick={() => handleApprove(user.id)}
            >
                Approve
            </Button>
        )

        if (!enabled) {
            const missingFields = getMissingFields(user)
            return (
                <OverlayTrigger
                    placement="left"
                    overlay={<Tooltip id={`tooltip-${user.id}`}>Missing fields: {missingFields.join(', ')}</Tooltip>}
                >
                    {/* Wrap button in span to allow disabled button to trigger tooltip */}
                    <span className="d-inline-block">{button}</span>
                </OverlayTrigger>
            )
        }

        return button
    }

    const handlePreviousPage = (): void => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1)
        }
    }

    const handleNextPage = (): void => {
        if (data?.getAllUsers && data.getAllUsers.length === limit) {
            setCurrentPage(currentPage + 1)
        }
    }

    // Define table columns with sorting
    const columns = useMemo<ColumnDef<User, any>[]>(
        () => [
            columnHelper.accessor(
                (row) => [row.firstName, row.lastName].filter(Boolean).join(' '),
                {
                    id: 'name',
                    header: 'Name',
                    cell: (info) => info.getValue() || '',
                    sortingFn: 'alphanumeric',
                }
            ),
            columnHelper.accessor('businessName', {
                header: 'Business Name',
                cell: (info) => info.getValue() || '',
                sortingFn: 'alphanumeric',
            }),
            columnHelper.accessor('email', {
                header: 'Email',
                cell: (info) => info.getValue() || '',
                sortingFn: 'alphanumeric',
            }),
            columnHelper.accessor('phone', {
                header: 'Phone',
                cell: (info) => info.getValue() || '',
                sortingFn: 'alphanumeric',
            }),
            columnHelper.accessor('createdAt', {
                header: 'Date of Registration',
                cell: (info) => formatDate(info.getValue()),
                sortingFn: 'datetime',
            }),
            columnHelper.accessor('onboarding', {
                header: 'Status',
                cell: (info) => {
                    const hasOnboarding = info.getValue()
                    return hasOnboarding ? (
                        <Badge bg="warning" text="dark">
                            New Customer
                        </Badge>
                    ) : (
                        <Badge bg="success">Active</Badge>
                    )
                },
                enableSorting: false,
            }),
            columnHelper.display({
                id: 'actions',
                header: 'Action',
                cell: (info) => renderApproveButton(info.row.original),
                enableSorting: false,
            }),
        ],
        []
    )

    // Initialize table instance
    const table = useReactTable({
        data: data?.getAllUsers || [],
        columns,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    })

    return (
        <Fragment>
            <Row>
                <Col sm="12">
                    <Card>
                        <Card.Header className="d-flex justify-content-between">
                            <div className="header-title">
                                <h4 className="card-title">User Management</h4>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {errors.length > 0 && (
                                <Alert variant="danger" dismissible onClose={() => setErrors([])}>
                                    {errors.map((error, key) => (
                                        <div key={key}>{error.message}</div>
                                    ))}
                                </Alert>
                            )}

                            {loading && (
                                <div className="tw:flex tw:items-center tw:justify-center tw:py-12">
                                    <CubeLoader size="default" />
                                </div>
                            )}

                            {error && !loading && (
                                <Alert variant="danger">Error loading users: {error.message}</Alert>
                            )}

                            {!loading && !error && data && (
                                <>
                                    <div className="tw:-mx-4 tw:overflow-x-auto md:tw:mx-0">
                                        <Table striped bordered hover className="tw:min-w-[720px] md:tw:min-w-full">
                                            <thead>
                                                {table.getHeaderGroups().map((headerGroup) => (
                                                    <tr key={headerGroup.id}>
                                                        {headerGroup.headers.map((header) => (
                                                            <th
                                                                key={header.id}
                                                                onClick={header.column.getToggleSortingHandler()}
                                                                style={{
                                                                    cursor: header.column.getCanSort()
                                                                        ? 'pointer'
                                                                        : 'default',
                                                                    userSelect: header.column.getCanSort()
                                                                        ? 'none'
                                                                        : 'auto',
                                                                }}
                                                            >
                                                                <div className="d-flex align-items-center gap-2">
                                                                    {flexRender(
                                                                        header.column.columnDef.header,
                                                                        header.getContext()
                                                                    )}
                                                                    {header.column.getCanSort() && (
                                                                        <span style={{ fontSize: '0.75rem' }}>
                                                                            {{
                                                                                asc: '↑',
                                                                                desc: '↓',
                                                                            }[header.column.getIsSorted() as string] ?? '↕'}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </th>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </thead>
                                            <tbody>
                                                {table.getRowModel().rows.length > 0 ? (
                                                    table.getRowModel().rows.map((row) => (
                                                        <tr key={row.id}>
                                                            {row.getVisibleCells().map((cell) => (
                                                                <td key={cell.id}>
                                                                    {flexRender(
                                                                        cell.column.columnDef.cell,
                                                                        cell.getContext()
                                                                    )}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={table.getAllColumns().length} className="text-center">
                                                            No users found
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>

                                    {/* Pagination Controls */}
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div>Showing page {currentPage}</div>
                                        <div>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="me-2"
                                                onClick={handlePreviousPage}
                                                disabled={currentPage === 1}
                                            >
                                                Previous
                                            </Button>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={handleNextPage}
                                                disabled={!data.getAllUsers || data.getAllUsers.length < limit}
                                            >
                                                Next
                                            </Button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Fragment>
    )
})

Users.displayName = 'Users'
export default Users
